import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Sparkles } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../../lib/utils';
import { SpecraAssistAttribution } from '../ensigns/assist-attribution';
import { SpecraButton } from '../ensigns/button';
import { SpecraDiffText } from '../ensigns/diff-text';
import { SpecraIcon } from '../ensigns/icon';
import { SpecraSpinner } from '../ensigns/spinner';
import { SpecraStatusDot } from '../ensigns/status-dot';
import { SpecraText } from '../ensigns/typography';
import { SpecraCard, SpecraCardContent, SpecraCardDescription, SpecraCardFooter, SpecraCardHeader, SpecraCardTitle } from '../lieutenants/card';
import { SpecraInlineBanner } from '../lieutenants/inline-banner';
import { SpecraStatusPill } from '../lieutenants/status-pill';
const statusPill = {
    pending: {
        tone: 'info',
        text: 'Needs your review'
    },
    applying: {
        tone: 'info',
        text: 'Applying…'
    },
    accepted: {
        tone: 'success',
        text: 'Accepted'
    },
    partial: {
        tone: 'warning',
        text: 'Partly applied'
    },
    rejected: {
        tone: 'neutral',
        text: 'Rejected'
    },
    failed: {
        tone: 'danger',
        text: 'Failed'
    },
    stale: {
        tone: 'warning',
        text: 'Out of date'
    }
};
const itemTone = {
    pending: 'neutral',
    applied: 'success',
    failed: 'danger',
    skipped: 'warning'
};
const itemStatusText = {
    pending: 'Pending',
    applied: 'Applied',
    failed: 'Failed',
    skipped: 'Skipped'
};
function hasValue(v) {
    return v !== undefined && v !== null && v !== '' && v !== false;
}
function ItemBody({ item }) {
    if (item.segments && item.segments.length > 0) {
        return /*#__PURE__*/ _jsx(SpecraDiffText, {
            segments: item.segments,
            size: "meta"
        });
    }
    if (hasValue(item.before)) {
        // Plain text values read as one diff: the old value struck, the new one marked.
        if (typeof item.before === 'string' && typeof item.after === 'string') {
            return /*#__PURE__*/ _jsx(SpecraDiffText, {
                segments: [
                    {
                        op: 'delete',
                        text: item.before
                    },
                    {
                        op: 'equal',
                        text: ' → '
                    },
                    {
                        op: 'insert',
                        text: item.after
                    }
                ],
                size: "meta"
            });
        }
        return /*#__PURE__*/ _jsxs(SpecraText, {
            as: "span",
            className: "break-words",
            size: "caption",
            tone: "secondary",
            children: [
                /*#__PURE__*/ _jsxs("del", {
                    className: "text-muted-foreground",
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            className: "sr-only",
                            children: "before: "
                        }),
                        item.before
                    ]
                }),
                /*#__PURE__*/ _jsx("span", {
                    "aria-hidden": "true",
                    children: " → "
                }),
                /*#__PURE__*/ _jsx("span", {
                    className: "sr-only",
                    children: ", after: "
                }),
                /*#__PURE__*/ _jsx("span", {
                    className: "font-medium text-foreground",
                    children: item.after
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsx(SpecraText, {
        as: "span",
        className: "break-words font-medium",
        size: "caption",
        children: item.after
    });
}
/**
 * The review-and-accept card for a Specra Assist write (FND-27 AWR-2): what
 * Assist proposes, each item's before → after, and Accept / Edit / Reject.
 * A decided card names who accepted it, "<approver> via Specra Assist".
 */ export const SpecraAssistProposalCard = /*#__PURE__*/ React.forwardRef(({ acceptLabel, actionTestIds, className, decidedAt, decidedBy, editLabel = 'Edit', error, items, kind, maxVisibleItems = 5, onAccept, onEdit, onReject, preview, rejectLabel = 'Reject', status, summary, target, title, ...props }, ref)=>{
    const titleId = React.useId();
    // When the action row unmounts under the keyboard (accept/reject went through),
    // focus moves to the card so it is not lost to <body>.
    const sectionRef = React.useRef(null);
    const actionsHadFocus = React.useRef(false);
    const showActions = status === 'pending' || status === 'applying';
    const setRefs = React.useCallback((node)=>{
        sectionRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
    }, [
        ref
    ]);
    React.useEffect(()=>{
        if (showActions || !actionsHadFocus.current) return;
        actionsHadFocus.current = false;
        sectionRef.current?.focus();
    }, [
        showActions
    ]);
    const [expanded, setExpanded] = React.useState(false);
    const all = items ?? [];
    const visible = expanded ? all : all.slice(0, Math.max(0, maxVisibleItems));
    const hidden = all.length - visible.length;
    const pill = statusPill[status];
    const busy = status === 'applying';
    const hasBody = hasValue(summary) || hasValue(target) || all.length > 0 || hasValue(preview) || hasValue(error);
    const accept = acceptLabel ?? (kind === 'tracked_change' ? 'Accept as tracked changes' : 'Accept');
    return /*#__PURE__*/ _jsx("section", {
        "aria-labelledby": titleId,
        className: cn('min-w-0', className),
        "data-kind": kind,
        "data-status": status,
        ref: setRefs,
        tabIndex: -1,
        ...props,
        children: /*#__PURE__*/ _jsxs(SpecraCard, {
            className: "min-w-0 overflow-hidden",
            children: [
                /*#__PURE__*/ _jsxs(SpecraCardHeader, {
                    className: "flex-wrap",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraIcon, {
                            className: "text-info",
                            children: /*#__PURE__*/ _jsx(Sparkles, {})
                        }),
                        /*#__PURE__*/ _jsx(SpecraCardTitle, {
                            className: "min-w-0 flex-1 break-words",
                            id: titleId,
                            children: title
                        }),
                        /*#__PURE__*/ _jsx("output", {
                            "aria-atomic": "true",
                            "aria-live": "polite",
                            children: /*#__PURE__*/ _jsx(SpecraStatusPill, {
                                tone: pill.tone,
                                children: pill.text
                            })
                        })
                    ]
                }),
                hasBody ? /*#__PURE__*/ _jsxs(SpecraCardContent, {
                    className: "flex flex-col gap-[var(--space-3)]",
                    children: [
                        hasValue(summary) || hasValue(target) ? /*#__PURE__*/ _jsxs(SpecraCardDescription, {
                            className: "break-words",
                            children: [
                                summary,
                                hasValue(summary) && hasValue(target) ? ' · ' : null,
                                target
                            ]
                        }) : null,
                        visible.length > 0 ? /*#__PURE__*/ _jsx("ul", {
                            className: "m-0 flex list-none flex-col gap-[var(--space-2)] p-0",
                            children: visible.map((item)=>/*#__PURE__*/ _jsxs("li", {
                                    className: "flex min-w-0 flex-col gap-[var(--space-0-5)]",
                                    "data-item-id": item.id,
                                    "data-item-status": item.status,
                                    children: [
                                        /*#__PURE__*/ _jsx(SpecraText, {
                                            as: "div",
                                            className: "break-words",
                                            size: "body-sm",
                                            children: item.label
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "min-w-0 break-words",
                                            children: /*#__PURE__*/ _jsx(ItemBody, {
                                                item: item
                                            })
                                        }),
                                        item.status && item.status !== 'pending' ? /*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center gap-[var(--space-1-5)]",
                                            children: [
                                                /*#__PURE__*/ _jsx(SpecraStatusDot, {
                                                    tone: itemTone[item.status]
                                                }),
                                                /*#__PURE__*/ _jsx(SpecraText, {
                                                    as: "span",
                                                    size: "caption",
                                                    tone: item.status === 'failed' ? 'danger' : 'muted',
                                                    children: hasValue(item.message) ? item.message : itemStatusText[item.status]
                                                })
                                            ]
                                        }) : null
                                    ]
                                }, item.id))
                        }) : null,
                        hidden > 0 ? /*#__PURE__*/ _jsx("div", {
                            children: /*#__PURE__*/ _jsxs(SpecraButton, {
                                onClick: ()=>setExpanded(true),
                                size: "sm",
                                type: "button",
                                variant: "ghost",
                                children: [
                                    "Show ",
                                    hidden,
                                    " more"
                                ]
                            })
                        }) : null,
                        preview,
                        hasValue(error) ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                            tone: "danger",
                            variant: "framed",
                            children: error
                        }) : null
                    ]
                }) : null,
                showActions ? /*#__PURE__*/ _jsxs(SpecraCardFooter, {
                    className: "flex-col items-stretch sm:flex-row sm:items-center",
                    onBlur: (e)=>{
                        if (!e.currentTarget.contains(e.relatedTarget)) {
                            actionsHadFocus.current = false;
                        }
                    },
                    onFocus: ()=>{
                        actionsHadFocus.current = true;
                    },
                    children: [
                        /*#__PURE__*/ _jsxs(SpecraButton, {
                            className: "w-full sm:w-auto",
                            disabled: busy,
                            onClick: onAccept,
                            "data-testid": actionTestIds?.accept,
                            type: "button",
                            children: [
                                busy ? /*#__PURE__*/ _jsx(SpecraSpinner, {
                                    label: false,
                                    size: "sm"
                                }) : null,
                                accept
                            ]
                        }),
                        onEdit ? /*#__PURE__*/ _jsx(SpecraButton, {
                            className: "w-full sm:w-auto",
                            disabled: busy,
                            onClick: onEdit,
                            "data-testid": actionTestIds?.edit,
                            type: "button",
                            variant: "secondary",
                            children: editLabel
                        }) : null,
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            className: "w-full sm:w-auto",
                            disabled: busy,
                            onClick: onReject,
                            "data-testid": actionTestIds?.reject,
                            type: "button",
                            variant: "ghost",
                            children: rejectLabel
                        })
                    ]
                }) : null,
                (status === 'accepted' || status === 'partial') && hasValue(decidedBy) ? /*#__PURE__*/ _jsx(SpecraCardFooter, {
                    children: /*#__PURE__*/ _jsx(SpecraAssistAttribution, {
                        approver: decidedBy,
                        at: decidedAt,
                        via: "specra_assist"
                    })
                }) : null,
                status === 'rejected' && hasValue(decidedBy) ? /*#__PURE__*/ _jsx(SpecraCardFooter, {
                    children: /*#__PURE__*/ _jsxs(SpecraText, {
                        as: "span",
                        size: "caption",
                        tone: "secondary",
                        children: [
                            "Rejected by ",
                            decidedBy,
                            hasValue(decidedAt) ? /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    " · ",
                                    decidedAt
                                ]
                            }) : null
                        ]
                    })
                }) : null
            ]
        })
    });
});
SpecraAssistProposalCard.displayName = 'SpecraAssistProposalCard';
